import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Rocket } from "lucide-react";
import { API, authHeaders } from "./shared";
import TwilioStatusPanel from "./TwilioStatusPanel";

const GoLiveTab = () => {
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/golive-checklist`, authHeaders())
      .then((r) => setData(r.data))
      .catch((e) => console.error("Failed to load checklist:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    setBusyKey(item.key);
    try {
      await axios.put(`${API}/admin/golive-checklist/${item.key}`, { complete: !item.complete }, authHeaders());
      load();
    } catch {
      toast.error("Failed to update item");
    } finally { setBusyKey(""); }
  };

  if (!data) return <p className="font-sans text-sm text-brand-text-muted">Loading checklist...</p>;

  return (
    <div data-testid="golive-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-serif text-2xl text-brand-text flex items-center gap-2">
          <Rocket size={20} className="text-brand-gold" /> Go-Live Checklist
        </h2>
        <span data-testid="golive-progress" className={`font-sans text-sm font-semibold px-3 py-1.5 border ${data.ready ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-brand-gold/40 text-brand-gold bg-brand-gold/10"}`}>
          {data.complete_count} of {data.total} ready
        </span>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-6">
        {data.ready ? "Everything checks out — you're ready to publish!" : "Work through each item below. Items with a button are checked off by you; the rest update automatically."}
      </p>

      <div className="border border-brand-border divide-y divide-brand-border/50">
        {data.items.map((item) => (
          <div key={item.key} data-testid={`golive-item-${item.key}`} className="flex items-start gap-4 p-4 bg-brand-surface/50">
            {item.complete
              ? <CheckCircle2 size={18} className="text-green-400 shrink-0 mt-0.5" />
              : <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm text-brand-text font-medium">{item.label}</p>
              <p className="font-sans text-xs text-brand-text-muted mt-0.5">{item.detail}</p>
            </div>
            <span data-testid={`golive-status-${item.key}`} className={`font-sans text-[10px] uppercase tracking-wider px-2 py-1 border whitespace-nowrap ${item.complete ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
              {item.complete ? "Complete" : "Needs Attention"}
            </span>
            {item.manual && (
              <button
                data-testid={`golive-toggle-${item.key}`}
                onClick={() => toggle(item)}
                disabled={busyKey === item.key}
                className="font-sans text-[11px] px-3 py-1.5 border border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {item.complete ? "Undo" : "Mark done"}
              </button>
            )}
          </div>
        ))}
      </div>

      <TwilioStatusPanel />
    </div>
  );
};

export default GoLiveTab;
